import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { resolveVoiceById } from "../lib/api";
import { VoiceFlag, VoiceTags } from "./ui-bits";
import VoiceSelect from "./VoiceSelect";
import { Mic, ChevronRight, X, Loader2 } from "lucide-react";

/**
 * Botón compacto de voz + selector completo en popup (ago-2026). VoiceSelect por sí solo es un
 * catálogo entero (búsqueda, filtros, lista paginada) — embebido inline en un formulario ya
 * apretado (1-clic, Narración, configuración de canal) se comía la mitad del espacio solo para
 * mostrar la voz que YA está elegida. Aquí solo se ve la voz actual; el catálogo completo se
 * abre en un popup al hacer clic, mismo patrón de overlay que el resto de la app (fixed inset-0
 * + panel centrado — ver ChannelWizard/UpgradeModal).
 *
 * Mismas props que VoiceSelect (language, value, onChange) — sustituye 1:1 en cualquier sitio
 * que lo use. NO se aplicó en ChannelWizard.jsx a propósito: ese paso del wizard está DEDICADO
 * por completo a elegir voz (nada más compite por el espacio ahí), así que colapsarlo solo
 * añadiría un clic extra sin ganar nada.
 */
export default function VoicePickerButton({ language, value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!value) { setCurrent(null); return; }
    setLoading(true);
    resolveVoiceById(value, language).then((v) => { if (!cancelled) { setCurrent(v); setLoading(false); } });
    return () => { cancelled = true; };
  }, [value, language]);

  return (
    <div data-testid="voice-picker">
      <button type="button" data-testid="voice-picker-open" onClick={() => setOpen(true)}
        className="w-full text-left border-2 border-slate-200 rounded-lg p-3 flex items-center justify-between gap-2 bg-white hover:border-slate-300 transition-colors">
        <div className="min-w-0 flex items-center gap-2">
          {loading ? (
            <Loader2 size={18} className="animate-spin text-slate-400 shrink-0" />
          ) : (
            <Mic size={18} className="text-slate-400 shrink-0" />
          )}
          {current ? (
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <VoiceFlag voiceId={current.id} fallbackLang={language || "es"} voiceLang={current.language} />
                <span className="font-bold text-slate-900 truncate">{current.name}</span>
              </div>
              <VoiceTags v={current} />
            </div>
          ) : (
            <span className="font-semibold text-slate-500">
              {loading ? t("voiceCat.pickerLoading") : value ? t("voiceCat.pickerNotFound") : t("voiceCat.pickerEmpty")}
            </span>
          )}
        </div>
        <span className="shrink-0 flex items-center gap-1 text-sm font-bold text-blue-700"><ChevronRight size={16} /></span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm overflow-y-auto vb-scroll" onClick={() => setOpen(false)}>
          <div className="min-h-full flex items-start sm:items-center justify-center sm:p-6 lg:p-10">
            <div onClick={(e) => e.stopPropagation()}
              className="relative bg-white w-full sm:max-w-xl h-screen sm:h-auto sm:max-h-[85vh] sm:rounded-2xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between gap-3 p-5 pb-3 shrink-0">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Mic size={20} /> {t("voiceCat.pickerTitle")}</h3>
                <button data-testid="voice-picker-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 p-1"><X size={22} /></button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto vb-scroll px-5 pb-5">
                <VoiceSelect language={language} value={value} onChange={(v) => { onChange(v); setOpen(false); }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
